/**
 * Shared parsing/validation/cleaning for document numbers entered in the
 * onboarding panels (single and batch).
 *
 * One line = one document number. Spaces inside a line are formatting
 * (e.g. "DE 10 2010 054 526 A1") and are stripped before validating or
 * sending to the backend.
 */

// Country/office code, the number itself, and an optional kind code.
// Digit run is wide because national numbering varies a lot:
// EP1234567A1 (7) through DE102010054526A1 (12).
const FOREIGN_PATENT_PATTERN = /^[A-Z]{2,3}\d{4,14}(?:[A-Z]\d{0,2})?$/;

/** Split raw textarea input into one entry per line, with spaces removed. */
export function splitDocumentEntries(input: string): string[] {
  return input
    .split(/\r?\n/)
    .map((entry) => entry.replace(/\s+/g, ''))
    .filter(Boolean);
}

/**
 * Normalize a single entry into the id sent to the backend.
 *
 * Nothing downstream copes with the punctuation people type. "19/662,910" once reached
 * the worker intact and was used in a file path, where the slash became a directory
 * that did not exist; the same string then becomes an S3 key, where a slash reads as a
 * folder. So the number is reduced here, once, before it leaves the browser.
 *
 * Resource types:
 *   application     digits only. "19/662,910", "19-662-910" -> "19662910".
 *   patent          US utility, reissue, design or plant. "US 7,123,456 B2" -> "7123456",
 *                   "RE39,260" -> "RE39260", "D456,789" -> "D456789". The RE/D/PP prefix
 *                   is part of the number and is kept; only a trailing kind code goes.
 *   publication     "US 2005/0187306 A1" -> "20050187306". A ten-digit number is padded
 *                   to the eleven-digit form.
 *   us_reference    a patent or publication where the caller does not know which. Both
 *                   rules above apply, and the length decides the padding.
 *   foreign_patent  "EP 1234567 A1" -> "EP1234567A1". Country and kind codes are meaning,
 *                   not punctuation - the EPO lookup needs them - so only separators go.
 *
 * The trailing kind code is stripped only when it follows a digit. An earlier version
 * used `[A-Z]\d*$`, which on "D456789" matched the whole thing and on "RE39260" matched
 * "E39260" - leaving "" and "R". Design and reissue patents were silently destroyed.
 */
export function cleanDocumentNumber(id: string, resourceType: string): string {
  let cleaned = id.replace(/\s+/g, '').replace(/,/g, '').replace(/\//g, '');

  if (resourceType === 'foreign_patent') {
    // Keep the full format (e.g. EP1234567A1), minus separators
    cleaned = cleaned.replace(/-/g, '').replace(/\./g, '').toUpperCase();
  } else if (resourceType === 'application') {
    cleaned = cleaned.replace(/\D/g, '');
  } else if (['publication', 'patent', 'us_reference'].includes(resourceType)) {
    cleaned = cleaned.replace(/-/g, '').replace(/^US/i, '').toUpperCase();
    // A kind code is a letter, optionally followed by a digit, at the end and after a
    // digit: B2, E, A1. Anchoring on the preceding digit is what protects the RE/D/PP
    // prefix, which sits before the digits rather than after them.
    cleaned = cleaned.replace(/(?<=\d)[A-Z]\d?$/, '');

    // A ten-digit number starting with 2 is a publication missing its series zero:
    // 2005187306 -> 20050187306. No utility patent number has that shape.
    if (resourceType !== 'patent' && /^2\d{9}$/.test(cleaned)) {
      cleaned = cleaned.slice(0, 4) + '0' + cleaned.slice(4);
    }
  }

  return cleaned;
}

/** Validate raw textarea input; returns one message per bad entry. */
export function validateDocumentNumbers(input: string, resourceType: string): string[] {
  if (!input.trim()) return [];

  const errors: string[] = [];

  splitDocumentEntries(input).forEach((entry, index) => {
    const cleaned = cleanDocumentNumber(entry, resourceType);

    if (resourceType === 'foreign_patent') {
      if (!FOREIGN_PATENT_PATTERN.test(cleaned)) {
        errors.push(
          `Entry ${index + 1} "${entry}" does not match foreign patent format (e.g., EP1234567A1, DE102010054526A1, WO2024123456A1)`
        );
      }
    } else if (resourceType === 'publication') {
      if (!/^2\d{3}0?\d{6}$/.test(cleaned)) {
        errors.push(
          `Entry ${index + 1} "${entry}" does not match publication format (2DDDNNNNNN)`
        );
      }
    } else if (resourceType === 'patent') {
      if (!/^\d{7,8}$/.test(cleaned)) {
        errors.push(
          `Entry ${index + 1} "${entry}" does not match patent format (7 or 8 digits)`
        );
      }
    } else if (resourceType === 'application') {
      if (!/^\d{8}$/.test(cleaned)) {
        errors.push(
          `Entry ${index + 1} "${entry}" does not match application format (8 digits)`
        );
      }
    }
  });

  return errors;
}
